<template>
  <v-container>
    <avs-backButton
      v-if="!inDialog"
      backTo="Cfsis"
    />
    <AvsSteppers
      :value="tab"
      @change="v => (tab = v)"
    >
      <template v-slot:headers>
        <AvsStep
          step="1"
          :rules="[() => isFormValid.informations]"
        >
          {{ $t("commons.informations") }}
          <small v-show="!isFormValid.informations">{{ $t("commons.validation_issues") }}</small>
        </AvsStep>
        <v-divider></v-divider>
        <AvsStep
          step="2"
          :rules="[() => isFormValid.pictures]"
        >
          {{ $tc("commons.picture", 2) }}
          <small v-show="!isFormValid.pictures">{{ $t("commons.validation_issues") }}</small>
        </AvsStep>
        <v-divider></v-divider>
        <AvsStep step="3">
          {{ $t("commons.resolution") }}
        </AvsStep>
        <v-divider></v-divider>
        <AvsStep step="4">{{ $tc("commons.signature", 2) }}</AvsStep>
      </template>
      <template v-slot:items>
        <v-stepper-content step="1">
          <v-form
            ref="form-informations"
            v-model="isFormValid.informations"
            :readonly="isReadOnly"
            class="pt-2 mb-5"
            lazy-validation
          >
            <NonConformityInput
              v-model="cfsi.non_conformity"
              :clearable="!isReadOnly"
              :disabled="!!non_conformity"
              :readonly="!!non_conformity"
              :rules="rules.non_conformity"
              dense
              outlined
            ></NonConformityInput>

            <v-text-field
              v-model="cfsi.cfsi_reference"
              :rules="isDisable ? [] : rules.cfsi_reference"
              :disabled="isDisable"
              auto-grow
              clearable
              dense
              outlined
              :label="$t('commons.cfsi_reference')"
            />

            <div style="margin-bottom: 25px">
              <label>{{ $t("commons.description") }}</label>
              <ckeditor
                :editor="editor"
                :rules="rules.quick_description"
                :disabled="isDisable"
                tag-name="textarea"
                rows="4"
                v-model="cfsi.quick_description"
                :config="editorConfig"
                :label="$t('commons.description')"
                class="content-element"
              ></ckeditor>
            </div>

            <DatetimePicker
              v-model="cfsi.report_date"
              :min="minReportDate"
              :max="maxReportDate"
              :readonly="!!isReadOnly"
              :disabled="isDisable"
              :label="$t('commons.date.report_date')"
              :textFieldProps="{
                placeholder: $t('commons.date.report_date'),
                rules: rules.report_date
              }"
            />

            <v-select
              v-model="cfsi.status"
              :items="statusItems"
              :rules="rules.status"
              dense
              outlined
              :label="$t('commons.status.name')"
            ></v-select>

            <v-row>
              <v-col class="pb-0 mb-0">
                <v-checkbox
                  :disabled="isDisable"
                  v-model="cfsi.notify_oncreated"
                  @change="handleNotifyOnCreatedChange"
                >
                  <template v-slot:label>
                    <div>{{ $t("categories.inspection.send_notifications") }}</div>
                  </template>
                </v-checkbox>
              </v-col>
              <v-col class="pb-0 mb-0">
                <v-checkbox
                  :disabled="isDisable"
                  v-model="cfsi.notify_team_oncreated"
                  @change="handleNotifyTeamOnCreatedChange"
                >
                  <template v-slot:label>
                    <div>{{ $t("categories.inspection.send_select_team_notifications") }}</div>
                  </template>
                </v-checkbox>
              </v-col>
            </v-row>
            <v-select
              v-if="cfsi.notify_team_oncreated"
              :disabled="isDisable"
              v-model="cfsi.users"
              :items="users"
              item-text="email"
              item-value="id"
              :label="$t('commons.send_to')"
              multiple
              chips
              outlined
              persistent-hint
            ></v-select>
          </v-form>
        </v-stepper-content>

        <v-stepper-content step="2">
          <v-col
            cols="12"
            class="pt-0 d-flex justify-center"
          >
            <CfsiPictures
              :validate-counter="validateRequest.pictures"
              @validated="v => (isFormValid.pictures = v)"
              v-model="cfsi.pictures"
              :rules="rules.pictures"
              :inspection="cfsi.inspection"
              :disabled="isReadOnly"
              :isDisable="isDisable"
            />
          </v-col>
        </v-stepper-content>

        <v-stepper-content step="3">
          <v-layout
            class="d-flex justify-center"
            v-if="loading"
          >
            <v-progress-circular
              indeterminate
              color="primary"
            ></v-progress-circular>
          </v-layout>
          <template v-else>
            <CfsiActionList
              :cfsi="cfsi"
              :projectId="projectId"
              v-if="cfsiExists"
            />
          </template>
        </v-stepper-content>

        <v-stepper-content step="4">
          <CfsiSign
            :cfsi="cfsi"
            :signatures="signatures"
            :isClosed="isClosed"
            @success="(v, m) => handleSuccess(v, 5, loadSignForm, m)"
            @error="handleError"
            @disableParent="handleSign"
            @cancel="handleClose"
            @confirm="handleConfirm"
          />
        </v-stepper-content>
      </template>
      <v-spacer></v-spacer>
      <template v-slot:actions>
        <avs-stepButton
          class="mx-2"
          icon="mdi-chevron-left"
          @click="tab--"
          :disabled="tab <= 1"
          :name="$t('commons.previous')"
        />
        <avs-stepButton
          class="mx-2"
          icon="mdi-chevron-right"
          right
          @click="tabIncrement"
          :name="nextLabel"
        />
      </template>
    </AvsSteppers>

    <avs-CloseDialog
      v-model="closeDialogIsOpen"
      @cancel="handleCancel"
      @confirm="handleConfirm"
    />
  </v-container>
</template>

<script>
import ClassicEditor from "@ckeditor/ckeditor5-build-classic"
import _ from "lodash"
const noop = () => undefined
import { CfsiService, PictureService, ProjectService } from "@/services"
import { STATUS_ITEMS, STATUS_ITEMS_DISABLED } from "@/utils/consts"
import { mkCfsi } from "@/utils/datamodels"

import { stringToDate } from "@/utils/datetime"
import { isInspector } from "@/utils/middleware"
import { TeamService } from "@/services"

import {
  // DateInput,
  NonConformityInput,
  CfsiPictures,
  CfsiActionList
} from "@/components"
import { required } from "@/utils/validator"

export default {
  name: "CfsiCreateUpdate",
  components: {
    AvsStep: () => import("@/components/generics/general/AvsStep.vue"),
    AvsSteppers: () => import("@/components/generics/general/AvsSteppers.vue"),
    DatetimePicker: () => import("@/components/inputs/DatetimePicker.vue"),
    CfsiSign: () => import("@/components/ri3/cfsi/CfsiSign.vue"),
    CfsiPictures,
    CfsiActionList,
    // DateInput,
    NonConformityInput
  },
  props: {
    inDialog: {
      type: Boolean,
      default: false
    },
    inDialogParams: {
      type: Object,
      default: () => {}
    }
  },
  data: function () {
    return {
      cfsiRef: null,
      cfsi: mkCfsi(),
      tab: this.$route.params.tab ? this.$route.params.tab : 1,
      nextLabel: this.$t("commons.next"),
      loading: false,
      step: -1,
      minReportDate: null,
      maxReportDate: stringToDate(),
      rules: {
        non_conformity: [],
        cfsi_reference: [required],
        quick_description: [],
        report_date: [required],
        status: [required],
        due_to: [],
        corrective_action: [],
        preventive_action: [],
        pictures: []
      },
      isFormValid: {
        informations: false,
        pictures: false,
        causeAndResolution: false
      },
      validateRequest: {
        pictures: 0
      },
      areasData: {},
      signatures: undefined,
      isDisable: false,
      isClosed: false,
      closeDialogIsOpen: false,
      users: [],
      editor: ClassicEditor,
      editorConfig: {
        toolbar: [
          "paragraph",
          "heading1",
          "heading2",
          "bold",
          "italic",
          "|",
          "link",
          "|",
          "undo",
          "redo",
          "-",
          "numberedList",
          "bulletedList"
        ]
      }
    }
  },
  created() {
    this.fetchCfsi()
    this.fetchAreas()
    this.fetchUsers()
  },
  methods: {
    deleteItem(type, value) {
      const params = { type, value }
      ProjectService.areas.delete(this, this.projectId, params).then(response => {
        this.fetchAreas()
      })
    },
    save() {
      this.$refs["form-informations"].validate()
      this.validateRequest.pictures++
      this.$nextTick(() => {
        this.cfsi.lang = this.$i18n.locale
        if (this.isFormValid.informations && this.isFormValid.pictures)
          if (this.cfsiExists) {
            this.updateCfsi()
          } else {
            this.createCfsi()
          }
        else {
          this.tab = 1
          const message = this.$t("messages.warning.all_required_info")
          this.send(message, "warning")
        }
      })
    },
    tabIncrement() {
      return this.tab < 4 ? this.tab++ : this.$router.push({ name: "Cfsis" })
    },
    handleClose() {
      return this.$router.push({ name: "Cfsis" })
    },
    send(msg, color) {
      this.$store.commit("app/message", { msg, color })
    },
    getCfsi(id) {
      if (id) {
        let t = this
        CfsiService.get(this, this.projectId, id).then(r => {
          t.cfsi = r
          t.cfsiRef = _.cloneDeep(t.cfsi)
          if (t.cfsi.state === 4) {
            t.isDisable = true
          }
          if (t.cfsi.status == "Closed") {
            t.isClosed = true
          }
        })
      }
    },
    createCfsi() {
      let tmp = _.cloneDeep(this.cfsi)

      // We rename "file_url" to "file" for the create to work
      tmp.pictures = tmp.pictures.map(picture => {
        picture.file = picture.file_url
        delete picture.file_url
        return picture
      })

      this.loading = true
      CfsiService.create(this, this.projectId, tmp)
        .then(response => {
          this.cfsi = response
          this.cfsiRef = _.cloneDeep(response)
          this.send(
            this.$t("messages.success.saved", {
              entity: this.$tc("categories.cfsi.name", 1)
            }),
            "success"
          )
        })
        .catch(err => {
          if (err.response && err.response.status === 403) {
            // alert("Access denied: You do not have permission to access this resource.")
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.permission_denied"),
              color: "error"
            })
          } else {
            let message = ""
            for (const e in err.response.data) {
              message += "- " + e + " : " + err.response.data[e] + "\n"
            }
            this.$store.commit("app/message", {
              msg: message,
              color: "error"
            })
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    updateCfsi() {
      const requests = []
      const picturesToCreate = []
      //  Create pictures that has no id
      //  Update pictures that were edited (has id)
      //  Delete pictures (has deleted==true)
      this.cfsi.pictures.map(p => {
        if (p.id) {
          if (p.removed) {
            requests.push(PictureService.remove(this, p.id))
          } else {
            const refPicture = this.cfsiRef.pictures.find(refP => refP.id === p.id)
            if (p.unassigned === true) {
              let obj = {
                non_conformity: this.cfsi.id,
                name: p.name
              }
              if (p.file_url.startsWith("data")) {
                obj["file"] = p.file_url
              }
              requests.push(PictureService.update(this, p.id, obj))
            } else if (
              !_.isEqual(_.pick(p, ["name", "file_url"]), _.pick(refPicture, ["name", "file_url"]))
            ) {
              let updatedPic = _.pick(p, ["name", "file_url"])
              if (updatedPic.file_url != refPicture.file_url) {
                updatedPic["file"] = updatedPic["file_url"]
                delete updatedPic["file_url"]
              }
              requests.push(PictureService.update(this, p.id, updatedPic))
            }
          }
        } else {
          picturesToCreate.push(_.pick(p, ["name", "file_url"]))
        }
      })
      if (picturesToCreate.length > 0) {
        let mappedPictures
        mappedPictures = picturesToCreate.map(p => {
          return {
            name: p.name,
            file: p.file_url
          }
        })
        requests.push(
          PictureService.create(this, this.cfsi.id, {
            files: mappedPictures
          })
        )
      }
      if (!_.isEqual(_.omit(this.cfsi, ["pictures"]), _.omit(this.cfsiRef, ["pictures"]))) {
        requests.push(CfsiService.update(this, this.cfsi.id, _.omit(this.cfsi, ["pictures"])))
      }
      if (requests.length > 0) {
        this.loading = true
        Promise.all(requests)
          .then(() => {
            this.send(
              this.$t("messages.success.updated", {
                entity: this.$tc("categories.cfsi.name", 1)
              }),
              "success"
            )
          })
          .catch(err => {
            let message = ""
            for (const e in err.response.data) {
              message += "- " + e + " : " + err.response.data[e] + "\n"
            }
            this.send(message, "error")
          })
          .finally(() => {
            this.loading = false
          })
      } else {
        this.send(this.$t("messages.success.no_updated"), "info")
      }
    },
    fetchCfsi() {
      // Load Cfsi
      this.getCfsi(this.id)
    },
    fetchAreas() {
      // Load project/company areas
      ProjectService.areas.get(this, this.projectId).then(data => {
        this.areasData = data
      })
    },
    handleError(msg) {
      // Handle message display
      this.$store.commit("app/message", { msg, color: "error" })
    },
    handleSign() {
      // Handle message display
      this.isDisable = true
    },
    handleSuccess(isSaved, nextTab, postProcess = noop, message = null) {
      // Handle message display
      const msg_success = message ? message : this.$t("messages.success.operation")
      const data = isSaved
        ? { msg: msg_success, color: "success" }
        : { msg: this.$t("messages.success.no_updated"), color: "info" }
      this.$store.commit("app/message", data)

      // Handle next display
      this.tab = nextTab

      // Postprocess
      postProcess()
    },
    handleConfirm() {
      CfsiService.close(this, this.cfsi.id)
        .then(r => {
          this.$store.commit("app/message", {
            msg: this.$t("messages.success.closed", {
              entity: this.$tc("categories.cfsi.name", 1)
            }),
            color: "success"
          })
          this.closeDialogIsOpen = false
          this.isClosed = true
        })
        .catch(err => {
          if (err.response && err.response.status === 403) {
            // alert("Access denied: You do not have permission to access this resource.")
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.permission_denied"),
              color: "error"
            })
          }
        })
      this.closeDialogIsOpen = false
    },
    handleCancel() {
      this.closeDialogIsOpen = false
    },
    async fetchCfsiSignatures() {
      this.signatures = await CfsiService.getSignatures(this.cfsi.id)
    },
    async loadSignForm() {
      if (this.cfsiExists) {
        this.loading = true
        this.fetchCfsiSignatures().finally(() => {
          this.loading = false
        })
      }
    },
    handleNotifyOnCreatedChange() {
      if (this.cfsi.notify_oncreated) {
        this.cfsi.notify_team_oncreated = false
        this.cfsi.users = []
      }
    },
    handleNotifyTeamOnCreatedChange() {
      if (this.cfsi.notify_team_oncreated) {
        this.cfsi.notify_oncreated = false
      } else {
        this.cfsi.users = []
      }
    },
    fetchUsers() {
      TeamService.getTeam(this, this.projectId).then(response => {
        this.users = response
      })
    }
  },
  watch: {
    tab(newValue, oldValue) {
      if (oldValue === 1 && newValue === 2) {
        this.tab = this.$refs["form-informations"].validate() ? 2 : 1
      } else if (oldValue === 2 && newValue === 3) {
        this.tab = this.isFormValid.pictures ? 3 : 2
        this.validateRequest.pictures++
        this.save()
      } /*else if (oldValue === 2 && newValue === 3) {
        this.save()
      }*/

      // Set Save label
      if (newValue === 2) {
        this.nextLabel = this.$t("commons.save_next") //NEXT.SAVE
      } else if (newValue === 4) {
        this.loadSignForm()
        this.nextLabel = this.$t("commons.finish") //NEXT.FINISH
      } else if (newValue === 3) {
        this.nextLabel = this.$t("commons.next")
      } else {
        this.nextLabel = this.$t("commons.next") //NEXT.DEFAULT
      }
    },
    "cfsi.non_conformity": {
      handler(newValue, oldValue) {
        if (newValue && newValue !== oldValue) {
          this.minReportDate = stringToDate(newValue.start_date)
        }
      },
      deep: true
    }
  },
  computed: {
    defaultAreaItems() {
      return Object.keys(this.areasData)
    },
    defaultEventItems() {
      const eventsData = this.areasData[this.cfsi.area] ?? {}
      return Object.keys(eventsData)
    },
    defaultOtherItems() {
      const eventsData = this.areasData[this.cfsi.area] ?? {}
      return eventsData[this.cfsi.event] ?? []
    },
    statusItems() {
      return isInspector(this.projectRole) ? STATUS_ITEMS : STATUS_ITEMS_DISABLED
    },
    id() {
      return this.inDialog ? this.inDialogParams?.id : this.$route.params.id || null
    },
    non_conformity() {
      return this.$route.query.non_conformity || this.$route.params.id || null
    },
    // A cfsi is read-only if its closed_date is null for operators
    isReadOnly() {
      return !!this.cfsi.closed_at && !isInspector(this.projectRole)
    },
    cfsiExists() {
      return this.cfsi.id > 0
    },
    projectId() {
      return this.$store.state.app.project.id
    },
    projectRole() {
      return this.$store.state.app.project.role
    }
  }
}
</script>

<style lang="scss">
.custom-container {
  display: flex;
  flex-direction: column;
  height: 80vh;
}
.stepper-item {
  flex: 1 1 auto;
  overflow-y: auto;
}
.ck.ck-editor__main > .ck-editor__editable {
  height: 130px;
}
.ck.ck-balloon-panel.ck-powered-by-balloon .ck.ck-powered-by {
  display: none !important;
}
.ck-rounded-corners .ck.ck-editor__main > .ck-editor__editable,
.ck.ck-editor__main > .ck-editor__editable.ck-rounded-corners {
  border-radius: var(--ck-border-radius);
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-bottom-right-radius: 10px;
  border-bottom-left-radius: 10px;
}

.ck-rounded-corners .ck.ck-editor__top .ck-sticky-panel .ck-toolbar,
.ck.ck-editor__top .ck-sticky-panel .ck-toolbar.ck-rounded-corners {
  border-radius: var(--ck-border-radius);
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}

.ck-editor__editable {
  color: rgba(0, 0, 0, 0.87) !important;
}
</style>
